import {
  Box,
  Badge,
  Flex,
  IconButton,
  Dialog,
  Text,
  Button,
  Spinner,
  Separator,
} from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import {
  ExclamationTriangleIcon,
  DotsHorizontalIcon,
  TrashIcon,
  FileTextIcon,
  ResetIcon,
} from "@radix-ui/react-icons";
import {
  deleteMarketingActivity,
  fetchDistinctValuesForMarketingActivities,
  type MarketingActivity,
} from "../../../state/marketing-activities";
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../../lib/supabase";
import { Manager } from "../../Manager";
import { useManagerContext } from "../../Manager/context";
import type { FetchResult, SearchParams } from "../../Manager/types";
import {
  searchPeriods,
  fetchPeriodById,
  type Period,
} from "../../../state/periods";
import {
  searchProducts,
  fetchProductById,
  type Product,
} from "../../../state/products";
import {
  searchStoreSets,
  fetchStoreSetsByIds,
  type StoreSet,
} from "../../../state/store-sets";
import {
  useRequirementTemplates,
  type RequirementTemplate,
} from "../../../state/suppliers";
import { RequirementTemplateSection } from "../../RequirementTemplateSection";
import type { ProductTemplateLabels } from "../../ProductRequirementLabelEditor";
import type { Json } from "../../../types/supabase";

type MarketingActivityType = {
  id: number;
  name: string;
};

/**
 * Inner component that syncs the template dropdown when a product is selected.
 * Resolves product → brand → supplier → requirement_template_id and auto-fills.
 */
function ProductTemplateSync() {
  const { formValues, setFormValue, selectedItem, dialogOpen } =
    useManagerContext<MarketingActivity>();
  const productId = formValues.product_id as number | null;

  useEffect(() => {
    if (!dialogOpen || !productId) return;

    // In edit mode, don't override if product hasn't changed
    if (selectedItem && selectedItem.product_id === productId) return;

    let cancelled = false;

    async function resolve() {
      const { data, error } = await supabase
        .from("products")
        .select("brands(suppliers(requirement_template_id))")
        .eq("id", productId!)
        .single();

      if (cancelled || error || !data) return;

      const templateId = (
        data.brands as unknown as {
          suppliers: { requirement_template_id: number } | null;
        } | null
      )?.suppliers?.requirement_template_id;

      if (templateId) {
        setFormValue("requirement_template_id", templateId.toString());
      }
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, productId, selectedItem, setFormValue]);

  return null;
}

/**
 * Inner component that determines whether the form should be read-only
 * based on the selected marketing activity's confirmed_at status.
 */
function useIsConfirmed(): boolean {
  const { selectedItem } = useManagerContext<MarketingActivity>();
  return !!selectedItem?.confirmed_at;
}

/**
 * Captures the Manager's refresh function into a ref for use outside the context.
 */
function RefreshCapture({
  refreshRef,
}: {
  refreshRef: React.MutableRefObject<(() => void) | null>;
}) {
  const { refresh } = useManagerContext<MarketingActivity>();
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh, refreshRef]);
  return null;
}

/**
 * Inner component that renders the form fields with readOnly/disabled
 * awareness based on confirmation status.
 */
function MAFormFields({
  t,
  templates,
  activityTypes,
}: {
  t: (key: string) => string;
  templates: RequirementTemplate[];
  activityTypes: MarketingActivityType[];
}) {
  const isConfirmed = useIsConfirmed();

  return (
    <>
      <ProductTemplateSync />
      <Manager.AutocompleteField<Period>
        name="period_id"
        label={t("marketingActivities.period") + " *"}
        placeholder={t("marketingActivities.periodPlaceholder")}
        readOnly={isConfirmed}
        getValue={(item) => (item as MarketingActivity).period_id}
        searchFn={searchPeriods}
        fetchByIdFn={fetchPeriodById}
        getOptionLabel={(p) => p.name}
        getOptionValue={(p) => p.id}
      />
      <Manager.AutocompleteField<Product>
        name="product_id"
        label={t("marketingActivities.product") + " *"}
        placeholder={t("marketingActivities.productPlaceholder")}
        readOnly={isConfirmed}
        getValue={(item) => (item as MarketingActivity).product_id}
        searchFn={searchProducts}
        fetchByIdFn={fetchProductById}
        getOptionLabel={(p) =>
          `${p.name}${p.brands ? ` - ${p.brands.name}` : ""}`
        }
        getOptionValue={(p) => p.id}
      />
      <Manager.MultiAutocompleteField<StoreSet>
        name="store_set_ids"
        label={t("marketingActivities.storeSets")}
        placeholder={t("marketingActivities.storeSetsPlaceholder")}
        readOnly={isConfirmed}
        getValue={(item) => {
          const activity = item as MarketingActivity;
          return (
            (activity.marketing_activities_store_sets
              ?.map((mass) => mass.store_set?.id)
              .filter(Boolean) as number[]) ?? []
          );
        }}
        searchFn={searchStoreSets}
        fetchByIdsFn={fetchStoreSetsByIds}
        getOptionLabel={(s) => `${s.name} (${s.store_count} stores)`}
        getOptionValue={(s) => s.id}
      />
      <Manager.SelectField<MarketingActivityType>
        name="type_id"
        label={t("marketingActivities.activityType") + " *"}
        options={activityTypes}
        getOptionLabel={(at) => at.name}
        getOptionValue={(at) => at.id.toString()}
        getValue={(item) =>
          (item as MarketingActivity).type_id?.toString() ?? ""
        }
        placeholder={t("marketingActivities.activityTypePlaceholder")}
      />
      <Manager.SelectField<RequirementTemplate>
        name="requirement_template_id"
        label={t("marketingActivities.requirementTemplate")}
        options={templates}
        getOptionLabel={(tmpl) => tmpl.name}
        getOptionValue={(tmpl) => tmpl.id.toString()}
        getValue={(item) =>
          (item as MarketingActivity).requirement_template_id?.toString() ?? ""
        }
        placeholder={t("marketingActivities.selectTemplate")}
        disabled={isConfirmed}
      />
      <MATemplateLabelSection />
      <Manager.TextField
        name="budget"
        label={t("marketingActivities.budget")}
        type="number"
        placeholder={t("marketingActivities.budgetPlaceholder")}
        readOnly={isConfirmed}
        getValue={(item) =>
          (item as MarketingActivity).budget?.toString() ?? ""
        }
      />
      <Manager.TextField
        name="target"
        label={t("marketingActivities.targetPercent")}
        type="number"
        placeholder={t("marketingActivities.targetPlaceholder")}
        getValue={(item) =>
          (item as MarketingActivity).target?.toString() ?? ""
        }
      />
    </>
  );
}

/**
 * Inner component that lives inside Manager.FormDialog to access context.
 * Shows the RequirementTemplateSection for MA create/edit.
 */
function MATemplateLabelSection() {
  const { selectedItem, formValues } = useManagerContext<MarketingActivity>();

  const currentTemplateId = formValues.requirement_template_id
    ? parseInt(formValues.requirement_template_id as string, 10)
    : null;

  // For edit: use the item's saved values. For create: null.
  const savedTemplateId = selectedItem?.requirement_template_id ?? null;
  const savedLabels =
    (selectedItem?.product_template_labels as ProductTemplateLabels) ?? null;

  // Labels are saved inline (accumulated in form state) and persisted on submit
  const { setFormValue } = useManagerContext<MarketingActivity>();

  const handleLabelsChange = (newLabels: ProductTemplateLabels) => {
    setFormValue("product_template_labels", newLabels);
  };

  // Need a template selected to show anything
  if (!currentTemplateId && !savedTemplateId) return null;

  return (
    <RequirementTemplateSection
      savedTemplateId={savedTemplateId}
      currentTemplateId={currentTemplateId}
      savedLabels={savedLabels}
      onLabelsChange={handleLabelsChange}
    />
  );
}

async function fetchMarketingActivities(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
): Promise<FetchResult<MarketingActivity>> {
  const filters = searchParams?.filters;

  // Build the select query - use inner joins when filtering requires them
  const needsSupplierFilter =
    Array.isArray(filters?.supplier_ids) && filters!.supplier_ids.length > 0;

  const brandPart = needsSupplierFilter
    ? `brand:brands!inner(
          *,
          supplier:suppliers!inner(*)
        )`
    : `brand:brands(
          *,
          supplier:suppliers(*)
        )`;

  const selectQuery = `
      *,
      period:periods(*),
      product:products!inner(
        *,
        ${brandPart}
      ),
      marketing_activity_type:marketing_activity_types(*),
      marketing_activities_store_sets(
        store_set:store_sets(
          *,
          stores_store_sets(*)
        )
      ),
      store_activities(*)
    `;

  const applyFilters = (query: any) => {
    let q = query.eq("is_deleted", false);

    if (filters?.period_ids && Array.isArray(filters.period_ids)) {
      q = q.in("period_id", filters.period_ids);
    }

    if (filters?.product_ids && Array.isArray(filters.product_ids)) {
      q = q.in("product_id", filters.product_ids);
    }

    if (filters?.supplier_ids && Array.isArray(filters.supplier_ids)) {
      q = q.in("product.brand.supplier_id", filters.supplier_ids);
    }

    if (filters?.type_ids && Array.isArray(filters.type_ids)) {
      q = q.in("type_id", filters.type_ids);
    }

    return q;
  };

  let query = applyFilters(
    supabase
      .from("marketing_activities")
      .select(selectQuery, { count: "exact" }),
  ).order("id", { ascending: false });

  if (limit !== undefined) {
    query = query.limit(limit);
  }
  if (offset !== undefined) {
    query = query.range(offset, offset + (limit ?? 10) - 1);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error("Error fetching marketing activities:", error);
    throw error;
  }

  const totalCount = count ?? 0;
  const hasMore =
    offset !== undefined && limit !== undefined
      ? offset + limit < totalCount
      : false;

  return {
    data: (data || []) as MarketingActivity[],
    hasMore,
    totalCount,
  };
}

/**
 * Row actions dialog – single "⋯" button that opens a dialog with all actions.
 */
function MAActionsButton({
  activity,
  onConfirmAndGenerate,
  onDownloadReport,
  onResetProofs,
  onError,
}: {
  activity: MarketingActivity;
  onConfirmAndGenerate: (id: number) => Promise<void>;
  onDownloadReport: (id: number) => Promise<void>;
  onResetProofs: (id: number) => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const { openDelete } = useManagerContext<MarketingActivity>();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isConfirmed = !!activity.confirmed_at;
  const busy = loading !== null;

  const runAction = async (key: string, fn: () => Promise<void>) => {
    setLoading(key);
    setError(null);
    onError(null);
    try {
      await fn();
      setOpen(false);
    } catch (err) {
      const message =
        err && typeof err === "object" && "message" in err
          ? (err as { message: string }).message
          : JSON.stringify(err);
      setError(message);
      onError(message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <IconButton
          variant="ghost"
          size="1"
          title={t("marketingActivities.actions")}
        >
          <DotsHorizontalIcon />
        </IconButton>
      </Dialog.Trigger>
      <Dialog.Content maxWidth="380px">
        <Dialog.Title>{t("marketingActivities.actions")}</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          {activity.product?.name} – {activity.period?.name}
        </Dialog.Description>

        <Flex direction="column" gap="2">
          {/* Planning Report */}
          {isConfirmed ? (
            <Button
              variant="soft"
              color="green"
              disabled={busy}
              onClick={() => {
                runAction("download", () => onDownloadReport(activity.id));
              }}
            >
              {loading === "download" ? <Spinner size="1" /> : <FileTextIcon />}
              {t("marketingActivities.downloadReport")}
            </Button>
          ) : (
            <Flex direction="column" gap="1">
              <Button
                variant="soft"
                color="blue"
                disabled={busy}
                onClick={() =>
                  runAction("confirm", () => onConfirmAndGenerate(activity.id))
                }
              >
                {loading === "confirm" ? (
                  <Spinner size="1" />
                ) : (
                  <FileTextIcon />
                )}
                {t("marketingActivities.confirmAndGenerate")}
              </Button>
              <Text size="1" color="gray" ml="1">
                {t("marketingActivities.confirmationWarning")}
              </Text>
            </Flex>
          )}

          {/* Reset Proofs */}
          <Button
            variant="soft"
            color="amber"
            disabled={busy}
            onClick={() => runAction("reset", () => onResetProofs(activity.id))}
          >
            {loading === "reset" ? <Spinner size="1" /> : <ResetIcon />}
            {t("marketingActivities.resetProofs")}
          </Button>

          <Separator size="4" />

          {/* Delete */}
          <Button
            variant="soft"
            color="red"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              openDelete(activity);
            }}
          >
            <TrashIcon />
            {t("common.delete")}
          </Button>
        </Flex>

        {error && (
          <Text color="red" size="1" mt="2">
            <ExclamationTriangleIcon /> {error}
          </Text>
        )}

        <Flex justify="end" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              {t("common.cancel")}
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function Activities() {
  const { t } = useTranslation();
  const templates = useRequirementTemplates();
  const [activityTypes, setActivityTypes] = useState<MarketingActivityType[]>(
    [],
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const refreshRef = useRef<(() => void) | null>(null);

  // Load activity types on mount
  useEffect(() => {
    async function loadActivityTypes() {
      const { data, error } = await supabase
        .from("marketing_activity_types")
        .select("*")
        .order("name");
      if (!error && data) {
        setActivityTypes(data);
      }
    }
    loadActivityTypes();
  }, []);

  const fetchData = useCallback(
    (limit?: number, offset?: number, searchParams?: SearchParams) =>
      fetchMarketingActivities(limit, offset, searchParams),
    [],
  );

  const fetchDistinctValues = useCallback(
    async (
      filterKey: string,
      otherFilters?: Record<string, unknown>,
    ): Promise<{ value: string | number; label: string }[]> => {
      return fetchDistinctValuesForMarketingActivities(filterKey, otherFilters);
    },
    [],
  );

  const getAcceptedProofsInfo = (
    storeActivities: { is_fulfilled: boolean }[] | undefined,
    target: number | null | undefined,
  ) => {
    if (!storeActivities?.length) return { count: 0, percentage: 0 };
    const count = storeActivities.filter((sa) => sa.is_fulfilled).length;

    if (target == null || target === 0) {
      return { count, percentage: 0 };
    }

    const targetCount = Math.round((storeActivities.length * target) / 100);
    const percentage =
      targetCount > 0 ? ((count / targetCount) * 100).toFixed(2) : "0.00";

    return { count, percentage };
  };

  const handleResetProofs = async (activityId: number) => {
    const { data: storeActivities, error: fetchError } = await supabase
      .from("store_activities")
      .select(
        "id, store_id, marketing_activity:marketing_activities!inner(period:periods!inner(start_date, end_date))",
      )
      .eq("marketing_activity_id", activityId);

    if (fetchError) {
      console.error("Error fetching store activities:", fetchError);
      throw fetchError;
    }

    const storeActivityIds = storeActivities?.map((sa) => sa.id) ?? [];

    if (storeActivityIds.length > 0) {
      // Delete product proofs (cascades to proofs table)
      const { data: productProofIds, error: productProofsError } =
        await supabase
          .from("product_proofs")
          .select("id")
          .in("store_activity_id", storeActivityIds);

      if (productProofsError) {
        console.error("Error fetching product proofs:", productProofsError);
        throw productProofsError;
      }

      if (productProofIds && productProofIds.length > 0) {
        const { error: deleteProductProofsError } = await supabase
          .from("proofs")
          .delete()
          .in(
            "id",
            productProofIds.map((p) => p.id),
          );

        if (deleteProductProofsError) {
          console.error(
            "Error deleting product proofs:",
            deleteProductProofsError,
          );
          throw deleteProductProofsError;
        }
      }

      // Delete store proofs for this activity's stores within period date range
      const period = storeActivities?.[0]?.marketing_activity?.period;
      const storeIds = [...new Set(storeActivities?.map((sa) => sa.store_id))];

      if (period && storeIds.length > 0) {
        // Get store proofs for these stores that were created within the period date range
        const { data: storeProofIds, error: storeProofsError } = await supabase
          .from("store_proofs")
          .select("id, proofs!inner(created_at)")
          .in("store_id", storeIds)
          .gte("proofs.created_at", period.start_date)
          .lte("proofs.created_at", period.end_date + "T23:59:59.999Z");

        if (storeProofsError) {
          console.error("Error fetching store proofs:", storeProofsError);
          throw storeProofsError;
        }

        if (storeProofIds && storeProofIds.length > 0) {
          const { error: deleteStoreProofsError } = await supabase
            .from("proofs")
            .delete()
            .in(
              "id",
              storeProofIds.map((p) => p.id),
            );

          if (deleteStoreProofsError) {
            console.error(
              "Error deleting store proofs:",
              deleteStoreProofsError,
            );
            throw deleteStoreProofsError;
          }
        }
      }

      // Reset is_fulfilled flag
      const { error: updateError } = await supabase
        .from("store_activities")
        .update({ is_fulfilled: false })
        .eq("marketing_activity_id", activityId);

      if (updateError) {
        console.error("Error updating store activities:", updateError);
        throw updateError;
      }
    }
  };

  const downloadReportFromStorage = useCallback(async (activityId: number) => {
    const { data, error } = await supabase.storage
      .from("planning-reports")
      .download(`${activityId}.pdf`);

    if (error) throw error;

    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `planning-report-${activityId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const handleConfirmAndGenerate = useCallback(
    async (activityId: number) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch("/api/planning-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ marketing_activity_id: activityId }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${response.status})`);
      }

      // Download the generated PDF
      await downloadReportFromStorage(activityId);

      // Refresh the list to show updated confirmation status
      refreshRef.current?.();
    },
    [downloadReportFromStorage],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: MarketingActivity | null,
  ) => {
    const periodId = data.period_id as number;
    const productId = data.product_id as number;
    const typeId = data.type_id as string;
    const storeSetIds = (data.store_set_ids as number[]) ?? [];
    const budget = data.budget ? parseFloat(data.budget as string) : null;
    const target = data.target ? parseInt(data.target as string, 10) : null;
    const templateId = data.requirement_template_id
      ? parseInt(data.requirement_template_id as string, 10)
      : null;

    if (item) {
      // For confirmed MAs, only allow updating type_id and target
      const isConfirmed = !!item.confirmed_at;

      if (isConfirmed) {
        const { error: updateError } = await supabase
          .from("marketing_activities")
          .update({
            type_id: parseInt(typeId, 10),
            target,
          })
          .eq("id", item.id);

        if (updateError) throw new Error(updateError.message);
        return;
      }

      // Update existing activity (not confirmed)
      const templateChanged =
        templateId != null && item.requirement_template_id !== templateId;

      // Build labels value: reset on template change, otherwise save form value
      let labelsValue: Json | null | undefined;
      if (templateChanged) {
        labelsValue = null;
      } else if (data.product_template_labels !== undefined) {
        labelsValue = data.product_template_labels as Json | null;
      }

      const { error: updateError } = await supabase
        .from("marketing_activities")
        .update({
          period_id: periodId,
          product_id: productId,
          type_id: parseInt(typeId, 10),
          budget,
          target,
          ...(templateId != null && { requirement_template_id: templateId }),
          ...(labelsValue !== undefined && {
            product_template_labels: labelsValue,
          }),
        })
        .eq("id", item.id);

      if (updateError) throw new Error(updateError.message);

      // Update store sets: delete existing, add new
      await supabase
        .from("marketing_activities_store_sets")
        .delete()
        .eq("marketing_activity_id", item.id);

      if (storeSetIds.length > 0) {
        const { error: storeSetError } = await supabase
          .from("marketing_activities_store_sets")
          .insert(
            storeSetIds.map((storeSetId) => ({
              marketing_activity_id: item.id,
              store_set_id: storeSetId,
            })),
          );
        if (storeSetError) throw new Error(storeSetError.message);
      }
    } else {
      // Create new activity
      // Use template from form, or resolve from supplier chain as fallback
      let requirementTemplateId = templateId;

      if (!requirementTemplateId) {
        const { data: productData, error: productError } = await supabase
          .from("products")
          .select("brands(suppliers(requirement_template_id))")
          .eq("id", productId)
          .single();

        if (productError) throw new Error(productError.message);

        requirementTemplateId =
          (
            productData?.brands as unknown as {
              suppliers: { requirement_template_id: number } | null;
            } | null
          )?.suppliers?.requirement_template_id ?? null;
      }

      if (!requirementTemplateId) {
        throw new Error(
          "Could not resolve requirement template from the product's supplier.",
        );
      }

      // Create new activity
      const { data: newActivity, error: createError } = await supabase
        .from("marketing_activities")
        .insert({
          period_id: periodId,
          product_id: productId,
          type_id: parseInt(typeId, 10),
          budget,
          target,
          is_deleted: false,
          requirement_template_id: requirementTemplateId,
          ...(data.product_template_labels !== undefined && {
            product_template_labels:
              data.product_template_labels as Json | null,
          }),
        })
        .select("id")
        .single();

      if (createError) throw new Error(createError.message);

      // Link store sets
      if (storeSetIds.length > 0) {
        const { error: storeSetError } = await supabase
          .from("marketing_activities_store_sets")
          .insert(
            storeSetIds.map((storeSetId) => ({
              marketing_activity_id: newActivity.id,
              store_set_id: storeSetId,
            })),
          );
        if (storeSetError) throw new Error(storeSetError.message);
      }
    }
  };

  const handleDelete = async (item: MarketingActivity) => {
    const result = await deleteMarketingActivity(item.id);
    if (!result.success) {
      throw new Error(result.error || "Failed to delete activity");
    }
  };

  const validateForm = (data: Record<string, unknown>): boolean => {
    const periodId = data.period_id as number | null;
    const productId = data.product_id as number | null;
    const typeId = data.type_id as string;
    return !!periodId && !!productId && !!typeId;
  };

  // Single actions dialog renderer
  const renderActionsButton = useCallback(
    (activity: MarketingActivity) => (
      <MAActionsButton
        activity={activity}
        onConfirmAndGenerate={handleConfirmAndGenerate}
        onDownloadReport={downloadReportFromStorage}
        onResetProofs={handleResetProofs}
        onError={setActionError}
      />
    ),
    [handleConfirmAndGenerate, downloadReportFromStorage],
  );

  return (
    <Box p="4">
      {actionError && (
        <Text color="red" size="2" mb="2">
          <ExclamationTriangleIcon /> {actionError}
        </Text>
      )}

      <Manager.Root<MarketingActivity>
        title={t("marketingActivities.title")}
        fetchData={fetchData}
      >
        <RefreshCapture refreshRef={refreshRef} />
        <Manager.CreateButton label={t("marketingActivities.createActivity")} />

        <Manager.Search>
          <Manager.DynamicSearch.Root fetchDistinctValues={fetchDistinctValues}>
            <Manager.DynamicSearch.Filter
              filterKey="period_ids"
              label={t("marketingActivities.period")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="product_ids"
              label={t("marketingActivities.product")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="supplier_ids"
              label={t("marketingActivities.supplier")}
            />
            <Manager.DynamicSearch.Filter
              filterKey="type_ids"
              label={t("marketingActivities.activityType")}
            />
          </Manager.DynamicSearch.Root>
        </Manager.Search>

        <Manager.Table>
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.period")}
            accessor={(a) => a.period?.name}
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.product")}
            accessor={(a) => a.product?.name}
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.supplier")}
            accessor={(a) => a.product?.brand?.supplier?.name}
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.activityType")}
            accessor={(a) => a.marketing_activity_type?.name}
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.totalStores")}
            accessor={(a) => a.store_activities?.length ?? 0}
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.budget")}
            accessor={(a) =>
              a.budget != null ? `$${a.budget.toLocaleString()}` : ""
            }
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.targetPercent")}
            accessor={(a) => (a.target != null ? `${a.target}%` : "")}
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.acceptedProofs")}
            accessor={(a) => {
              const { count, percentage } = getAcceptedProofsInfo(
                a.store_activities,
                a.target,
              );
              return `${count} (${percentage}%)`;
            }}
          />
          <Manager.Column<MarketingActivity>
            header={t("marketingActivities.confirmation")}
            accessor={(a) =>
              a.confirmed_at ? (
                <Badge color="green">
                  {t("marketingActivities.confirmed")}
                </Badge>
              ) : (
                <Badge color="gray">
                  {t("marketingActivities.notConfirmed")}
                </Badge>
              )
            }
          />
          <Manager.Actions>
            <Manager.EditAction />
            <Manager.CustomAction<MarketingActivity>
              id="row-actions"
              render={renderActionsButton}
            />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<MarketingActivity>
          title={(isEdit) =>
            isEdit
              ? t("marketingActivities.editActivity")
              : t("marketingActivities.createActivity")
          }
          description={(isEdit) =>
            isEdit
              ? t("marketingActivities.editDescription")
              : t("marketingActivities.createDescription")
          }
          onSubmit={handleSubmit}
          validate={validateForm}
        >
          <MAFormFields
            t={t}
            templates={templates}
            activityTypes={activityTypes}
          />
        </Manager.FormDialog>

        <Manager.DeleteDialog<MarketingActivity>
          getItemName={(a) =>
            `${a.product?.name ?? "Unknown"} - ${a.period?.name ?? "Unknown"}`
          }
          onDelete={handleDelete}
        />
      </Manager.Root>
    </Box>
  );
}
